import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  Tag,
  Space,
  Input,
  Select,
  Modal,
  Form,
  Popconfirm,
  Row,
  Col,
  Empty,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import userService from '../services/userService';
import { notify } from '../components/Customs/NotifyProvider';
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
  getRoleDisplayLabel,
} from '../utils/permissionUtils';

const { Search } = Input;
const { Option } = Select;

const MODULE_KEY = 'manage_user';

const defaultFormValues = {
  full_name: '',
  username: '',
  email: '',
  phone: '',
  password: '',
  role: 'waiter',
  status: 'active',
};

const ManageUser = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [openModal, setOpenModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [customRoles, setCustomRoles] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [addingRole, setAddingRole] = useState(false);

  const [form] = Form.useForm();

  const loadRoles = async () => {
    try {
      const res = await userService.listRoles();
      if (res?.status) {
        setCustomRoles(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      // Non-fatal — the built-in roles still work without this.
    }
  };

  const handleAddRole = async () => {
    const name = newRoleName.trim();
    if (!name) return;

    setAddingRole(true);
    try {
      const res = await userService.createRole(name);
      if (res?.status) {
        await loadRoles();
        form.setFieldsValue({ role: res.data.name });
        setNewRoleName('');
        notify.success('Role added');
      } else {
        notify.error(res?.message || 'Failed to add role');
      }
    } catch (error) {
      notify.error(error?.message || 'Failed to add role');
    } finally {
      setAddingRole(false);
    }
  };

  const handleDeleteRole = (id, name) => {
    Modal.confirm({
      title: 'Delete Role',
      content: `Delete the role "${name}"? This cannot be undone.`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await userService.deleteRole(id);
          if (res?.status) {
            notify.success('Role deleted');
            await loadRoles();
            if (roleFilter === name) setRoleFilter('');
            if (form.getFieldValue('role') === name) {
              form.setFieldsValue({ role: undefined });
            }
          } else {
            notify.error(res?.message || 'Failed to delete role');
          }
        } catch (error) {
          notify.error(error?.message || 'Failed to delete role');
        }
      },
    });
  };

  const loadUsers = async () => {
    if (!canViewModule(MODULE_KEY)) return;

    try {
      setLoading(true);
      const res = await userService.listUsers();

      if (res?.status) {
        setUsers(Array.isArray(res.data) ? res.data : []);
      } else {
        notify.error(res?.message || 'Failed to fetch users');
        setUsers([]);
      }
    } catch (error) {
      notify.error(error?.message || 'Failed to fetch users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const openAddModal = () => {
    if (!canCreateModule(MODULE_KEY)) {
      notify.error('Permission Denied', 'No permission to create user');
      return;
    }

    setEditingUser(null);
    form.setFieldsValue(defaultFormValues);
    setOpenModal(true);
  };

  const openEditModal = (record) => {
    if (!canUpdateModule(MODULE_KEY)) {
      notify.error('Permission Denied', 'No permission to update user');
      return;
    }

    setEditingUser(record);
    form.setFieldsValue({
      full_name: record.full_name || '',
      username: record.username || '',
      email: record.email || '',
      phone: record.phone || '',
      password: '',
      role: record.role || 'waiter',
      status: record.status || 'active',
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleSave = async (values) => {
    try {
      if (editingUser && !canUpdateModule(MODULE_KEY)) {
        notify.error('Permission Denied', 'No permission to update user');
        return;
      }

      if (!editingUser && !canCreateModule(MODULE_KEY)) {
        notify.error('Permission Denied', 'No permission to create user');
        return;
      }

      setSaving(true);

      const payload = {
        full_name: values.full_name,
        username: values.username,
        email: values.email || null,
        phone: values.phone || null,
        role: values.role,
        status: values.status,
      };

      if (values.password) {
        payload.password = values.password;
      }

      let res;

      if (editingUser) {
        res = await userService.updateUser(editingUser.user_id, payload);
      } else {
        res = await userService.createUser(payload);
      }

      // "Admin" is the highest role selectable here (Superadmin is created
      // only when a restaurant itself is created) — it's meant to default
      // to full access, so grant every permission up front. Unlike
      // Superadmin's hard-coded bypass, this stays editable afterwards via
      // User Permission if the restaurant's superadmin wants to restrict it.
      if (!editingUser && res?.status && values.role === 'admin' && res.data?.user_id) {
        try {
          const modulesRes = await userService.listModules();
          const modules = modulesRes?.data || [];

          const fullPermissions = modules.map((m) => ({
            module_key: m.module_key,
            can_view: 1,
            can_create: 1,
            can_update: 1,
            can_delete: 1,
          }));

          await userService.saveUserPermissions(res.data.user_id, fullPermissions);
        } catch {
          // Non-fatal — the user is still created; permissions can be set
          // manually via User Permission if this background step fails.
        }
      }

      if (res?.status) {
        notify.success(
          editingUser ? 'User updated successfully' : 'User created successfully'
        );
        handleCloseModal();
        loadUsers();
      } else {
        notify.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      notify.error(
        error?.response?.data?.message || error?.message || 'Failed to save user'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!canDeleteModule(MODULE_KEY)) {
      notify.error('Permission Denied', 'No permission to delete user');
      return;
    }

    try {
      const res = await userService.deleteUser(record.user_id);

      if (res?.status) {
        notify.success('User deleted successfully');
        loadUsers();
      } else {
        notify.error(res?.message || 'Delete failed');
      }
    } catch (error) {
      notify.error(
        error?.response?.data?.message || error?.message || 'Delete failed'
      );
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter((item) => {
      const q = searchText.toLowerCase();

      const matchesSearch =
        !searchText ||
        item.user_id?.toString().includes(searchText) ||
        item.full_name?.toLowerCase().includes(q) ||
        item.username?.toLowerCase().includes(q) ||
        item.email?.toLowerCase().includes(q) ||
        item.phone?.toLowerCase().includes(q) ||
        item.role?.toLowerCase().includes(q);

      const matchesRole = !roleFilter || item.role === roleFilter;
      const matchesStatus = !statusFilter || item.status === statusFilter;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchText, roleFilter, statusFilter]);

  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.status === 'active').length;
  const inactiveUsers = users.filter((u) => u.status === 'inactive').length;

  const columns = [
    {
      title: 'UID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
      render: (value) => <span className="uid-text">#{value}</span>,
    },
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (value) => value || '-',
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (value) => <span className="username-text">{value}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (value) => value || '-',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (value) => value || '-',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 130,
      render: (value) => {
        const colorMap = {
          superadmin: 'purple',
          admin: 'blue',
          manager: 'gold',
          waiter: 'green',
          checkout: 'cyan',
        };

        return <Tag color={colorMap[value] || 'default'}>{getRoleDisplayLabel(value)}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value) => (
        <Tag color={value === 'active' ? 'success' : 'default'}>
          {value}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space size={8}>
          <Button
            size="small"
            shape="circle"
            icon={<EditOutlined />}
            className="table-icon-btn edit-btn"
            onClick={() => openEditModal(record)}
            disabled={!canUpdateModule(MODULE_KEY)}
          />
          <Popconfirm
            title="Delete user"
            description="Are you sure you want to delete this user?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(record)}
            disabled={!canDeleteModule(MODULE_KEY)}
          >
            <Button
              size="small"
              shape="circle"
              icon={<DeleteOutlined />}
              className="table-icon-btn delete-btn"
              disabled={!canDeleteModule(MODULE_KEY)}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!canViewModule(MODULE_KEY)) {
    return (
      <div className="page-shell user-master-page">
        <div className="page-empty">
          <Empty description="No permission to view users" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <TeamOutlined />
          </div>

          <div>
            <h2 className="user-page-title">User Master</h2>
            <p className="user-page-subtitle">
              Manage system users, their roles, and account status from one place.
            </p>
          </div>
        </div>

        <div className="user-page-header-actions">
          <Button
            icon={<ReloadOutlined />}
            className="secondary-btn"
            onClick={loadUsers}
            loading={loading}
            disabled={!canViewModule(MODULE_KEY)}
          >
            Refresh
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="primary-btn"
            onClick={openAddModal}
            disabled={!canCreateModule(MODULE_KEY)}
          >
            Add User
          </Button>
        </div>
      </div>

      <Row gutter={[16, 16]} className="user-stats-row">
        <Col xs={24} sm={12} lg={8}>
          <div className="stats-card">
            <div className="stats-card-icon blue">
              <UserOutlined />
            </div>
            <div>
              <div className="stats-card-label">Total Users</div>
              <div className="stats-card-value">{totalUsers}</div>
            </div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <div className="stats-card">
            <div className="stats-card-icon green">
              <TeamOutlined />
            </div>
            <div>
              <div className="stats-card-label">Active Users</div>
              <div className="stats-card-value">{activeUsers}</div>
            </div>
          </div>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <div className="stats-card">
            <div className="stats-card-icon red">
              <UserOutlined />
            </div>
            <div>
              <div className="stats-card-label">Inactive Users</div>
              <div className="stats-card-value">{inactiveUsers}</div>
            </div>
          </div>
        </Col>
      </Row>

      <div className="page-card filter-card">
        <div className="filter-card-header">
          <h3>Filters</h3>
          <span>{filteredUsers.length} users found</span>
        </div>

        <div className="filters-inline-row">
          <div className="filter-item role-item">
            <label className="field-label">Role</label>
            <Select
              placeholder="All roles"
              allowClear
              className="page-select w-full"
              value={roleFilter || undefined}
              onChange={(value) => setRoleFilter(value || '')}
            >
              <Option value="admin">Admin (Default all permissions)</Option>
              <Option value="manager">Manager</Option>
              <Option value="waiter">Waiter</Option>
              {customRoles.map((r) => (
                <Option key={r.id} value={r.name}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{r.name}</span>
                    <DeleteOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRole(r.id, r.name);
                      }}
                      style={{ color: '#ff4d4f' }}
                    />
                  </div>
                </Option>
              ))}
            </Select>
          </div>

          <div className="filter-item status-item">
            <label className="field-label">Status</label>
            <Select
              placeholder="All statuses"
              allowClear
              className="page-select w-full"
              value={statusFilter || undefined}
              onChange={(value) => setStatusFilter(value || '')}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </div>

          <div className="filter-item search-item">
            <label className="field-label">Search</label>
            <Search
              placeholder="Search UID / username / email / role"
              allowClear
              className="page-search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>User List</h3>
            <p>View, edit, and control user access details.</p>
          </div>
        </div>

        <div className="common-table-wrap">
          <Table
            rowKey="user_id"
            columns={columns}
            dataSource={filteredUsers}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
            }}
            scroll={{ x: 1000 }}
          />
        </div>
      </div>

      <Modal
        title={editingUser ? 'Edit User' : 'Add User'}
        open={openModal}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnClose
        width={760}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={defaultFormValues}
        >
          <div className="form-grid-2">
            <Form.Item
              label="Full Name"
              name="full_name"
              rules={[{ required: true, message: 'Please enter full name' }]}
            >
              <Input placeholder="Enter full name" />
            </Form.Item>

            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true, message: 'Please enter username' }]}
            >
              <Input placeholder="Enter username" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[{ type: 'email', message: 'Please enter a valid email' }]}
            >
              <Input placeholder="Enter email" />
            </Form.Item>

            <Form.Item label="Phone" name="phone">
              <Input placeholder="Enter phone number" />
            </Form.Item>

            <Form.Item
              label={editingUser ? 'New Password' : 'Password'}
              name="password"
              rules={
                editingUser
                  ? []
                  : [{ required: true, message: 'Please enter password' }]
              }
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>

            <Form.Item
              label="Role"
              name="role"
              rules={[{ required: true, message: 'Please select role' }]}
            >
              <Select
                disabled={!canCreateModule(MODULE_KEY) && !canUpdateModule(MODULE_KEY)}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <div style={{ display: 'flex', gap: 8, padding: '0 8px 8px' }}>
                      <Input
                        placeholder="New role name"
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onPressEnter={handleAddRole}
                      />
                      <Button
                        type="primary"
                        loading={addingRole}
                        onClick={handleAddRole}
                      >
                        Add
                      </Button>
                    </div>
                  </>
                )}
              >
                {/* Superadmin is the restaurant's own bootstrap account — not
                    selectable for new/other users, but kept as an option here
                    so editing that one existing record doesn't show blank. */}
                {editingUser?.role === 'superadmin' && (
                  <Option value="superadmin">Superadmin</Option>
                )}
                <Option value="admin">Admin (Default all permissions)</Option>
                <Option value="manager">Manager</Option>
                <Option value="waiter">Waiter</Option>
                {customRoles.map((r) => (
                  <Option key={r.id} value={r.name}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{r.name}</span>
                      <DeleteOutlined
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRole(r.id, r.name);
                        }}
                        style={{ color: '#ff4d4f' }}
                      />
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select disabled={!canCreateModule(MODULE_KEY) && !canUpdateModule(MODULE_KEY)}>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>
          </div>

          <div className="form-actions">
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              className="primary-btn"
              loading={saving}
              disabled={
                editingUser
                  ? !canUpdateModule(MODULE_KEY)
                  : !canCreateModule(MODULE_KEY)
              }
            >
              {editingUser ? 'Update User' : 'Save User'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageUser;